<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import { useSiteDomains, DOMAIN_POLL_INTERVAL_MS } from '@/composables/useSiteDomains'
import { useSiteHostReadiness } from '@/composables/useSiteHostReadiness'
import {
	normalizeHostname, validateHostname, isApexDomain, baseDomainOf,
	domainPhase, domainPhaseLabel, domainPhaseVariant, humanizeDomainError, RECORD_PURPOSE_LABELS,
} from '@/Utils/siteDomains'
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import Alert from '@/components/Atoms/Alert.vue'
import Spinner from '@/components/ui/Spinner.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import SitesUpgradeModal from '@/components/Sites/SitesUpgradeModal.vue'
import { SITES_UPGRADE_REASONS } from '@/lib/tracking/sites'
import IconBase from '@/components/icon/IconBase.vue'
import IconGlobe from '@/components/icon/IconGlobe.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconLock from '@/components/icon/IconLock.vue'

// Custom domains managed by castr (control-plane API §6d). The owner adds a
// hostname, creates the DNS record(s) castr lists for it (`domain.records`:
// a CNAME for a subdomain; with the DigitalOcean App Platform runtime an apex
// gets A records to the app's ingress instead) and the domain turns Active
// once the routing provider sees the records and issues the certificate.
// Pending domains are re-checked every 30 s. `meta.provider` / `meta.ingressIps`
// come from newer castr builds; without them the copy stays CNAME-only.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})

const { notify } = useNotification()
const dom = useSiteDomains(() => props.siteId)
// The Castr address itself may still be routing on the edge right after creation.
const readiness = useSiteHostReadiness(() => props.siteId)
const addressPending = computed(() => readiness.gated.value)

// hostname → whether its DNS panel is open (pending domains open by default)
const expanded = ref({})

// Plan without `sites:custom-domain`: everything stays visible, add / check /
// set primary open the upgrade popup; remove and reverting to the Castr address still work.
// At the plan's `sites:max-custom-domains` only adding is blocked (same popup, limit copy).
const locked = computed(() => !dom.allowed.value)
const limitReached = computed(() => !locked.value && dom.loaded.value && dom.atLimit.value)
const upgradeVisible = ref(false)
const upgradeReason = ref('plan')
const showUpgrade = (reason = 'plan') => {
	upgradeReason.value = reason
	upgradeVisible.value = true
}
const blockedByPlan = (err) => {
	if (dom.markNotInPlan(err)) showUpgrade('plan')
	else if (dom.markLimit(err)) showUpgrade('limit')
	else return false
	return true
}
const domainsNoun = (n) => (n === 1 ? 'custom domain' : 'custom domains')
const upgradeCopy = computed(() => (upgradeReason.value === 'limit'
	? {
		title: 'Custom domain limit reached',
		body: `Your plan allows ${dom.meta.value.max} ${domainsNoun(dom.meta.value.max)} per site. Upgrade to connect more domains to this site, or remove one first.`,
	}
	: {
		title: 'Upgrade to use custom domains',
		body: 'Connecting your own domain to a site is not included in your current plan. Upgrade to add, verify or switch to a custom domain.',
	}))

// --- add form ---------------------------------------------------------------
const hostnameInput = ref('')
const touched = ref(false)
const adding = ref(false)
const addError = ref('')

const baseDomain = computed(() => baseDomainOf(props.site.primaryHost))
const normalized = computed(() => normalizeHostname(hostnameInput.value))
const validation = computed(() => validateHostname(normalized.value, {
	baseDomain: baseDomain.value,
	cnameTarget: dom.meta.value.cnameTarget,
	existing: dom.domains.value.map((d) => d.hostname),
}))
const showValidation = computed(() => touched.value && hostnameInput.value.trim().length > 0 && !validation.value.valid)
const apexNote = computed(() => Boolean(normalized.value) && validation.value.valid && isApexDomain(normalized.value))
const canSubmit = computed(() => dom.canAdd.value && validation.value.valid && !adding.value)
watch(hostnameInput, () => { addError.value = '' })

async function submit() {
	if (locked.value) return showUpgrade('plan')
	if (limitReached.value) return showUpgrade('limit')
	touched.value = true
	addError.value = ''
	if (!dom.canAdd.value || !validation.value.valid) return
	adding.value = true
	try {
		const domain = await dom.add(normalized.value)
		hostnameInput.value = ''
		touched.value = false
		if (domain?.hostname) expanded.value = { ...expanded.value, [domain.hostname]: true }
		notify({ group: 'success', text: `${domain?.hostname || normalized.value} added. Create the DNS record below to activate it.` })
	} catch (err) {
		if (err?.reason === 'custom_domains_unconfigured') {
			dom.unconfigured.value = true
			return
		}
		if (blockedByPlan(err)) return
		addError.value = err?.message || 'Could not add the domain.'
	} finally {
		adding.value = false
	}
}

// --- per-domain actions -----------------------------------------------------
const isExpanded = (domain) => expanded.value[domain.hostname] ?? domain.status !== 'active'
const toggle = (domain) => { expanded.value = { ...expanded.value, [domain.hostname]: !isExpanded(domain) } }

const isBusy = (domain, kind) => (kind ? dom.busy.value[domain.hostname] === kind : Boolean(dom.busy.value[domain.hostname]))

async function check(domain) {
	if (locked.value) return showUpgrade('plan')
	try {
		const res = await dom.verify(domain.hostname)
		if (res?.activated) {
			notify({ group: 'success', text: `${domain.hostname} is active. Your site is now served on it.` })
		} else if (res?.domain?.status === 'failed') {
			notify({ group: 'error', text: `${domain.hostname} could not be verified. Check the details on the card.` })
		} else {
			notify({ group: 'success', text: `${domain.hostname}: ${domainPhaseLabel(res?.domain).toLowerCase()} — DNS changes can take a few minutes to show up.` })
		}
	} catch (err) {
		if (blockedByPlan(err)) return
		notify({ group: 'error', text: err?.message || `Could not check ${domain.hostname}.` })
	}
}

const pendingRemove = ref(null)
const requestRemove = (domain) => { pendingRemove.value = domain; showModal('remove-site-domain') }
async function confirmRemove() {
	const domain = pendingRemove.value
	if (!domain) return
	try {
		await dom.remove(domain.hostname)
		notify({ group: 'success', text: `${domain.hostname} removed.` })
	} catch (err) {
		notify({ group: 'error', text: err?.message || `Could not remove ${domain.hostname}.` })
	} finally {
		pendingRemove.value = null
	}
}

async function makePrimary(domain) {
	if (locked.value) return showUpgrade('plan')
	try {
		await dom.setPrimary(domain.hostname)
		notify({ group: 'success', text: `${domain.hostname} is now the primary address of your site.` })
	} catch (err) {
		if (blockedByPlan(err)) return
		notify({ group: 'error', text: err?.message || 'Could not set the primary domain.' })
	}
}
const revertingPrimary = ref(false)
async function revertPrimary() {
	revertingPrimary.value = true
	try {
		await dom.setPrimary(props.site.primaryHost)
		notify({ group: 'success', text: `${props.site.primaryHost} is the primary address again.` })
	} catch (err) {
		notify({ group: 'error', text: err?.message || 'Could not change the primary address.' })
	} finally {
		revertingPrimary.value = false
	}
}

// --- derived -----------------------------------------------------------------
const cnameTarget = computed(() => dom.meta.value.cnameTarget || 'connect.sites.castr.io')
// Root domains get A records (instead of a CNAME) when castr runs the sites on
// App Platform and knows its ingress addresses; older castr builds / the
// Cloudflare provider only offer the CNAME, so the apex caveat stays.
const apexViaARecords = computed(() => dom.meta.value.provider === 'digitalocean' && (dom.meta.value.ingressIps || []).length > 0)
const canonicalHost = computed(() => dom.meta.value.canonicalHost || props.site.canonicalHost || props.site.primaryHost)
const castrIsPrimary = computed(() => !canonicalHost.value || canonicalHost.value === props.site.primaryHost)
const activeCount = computed(() => dom.domains.value.filter((d) => d.status === 'active').length)
const pollSeconds = Math.round(DOMAIN_POLL_INTERVAL_MS / 1000)

const errorsFor = (domain) => {
	const list = [...(domain.verificationErrors || []), ...(domain.validationErrors || [])]
	return [...new Set(list.map(humanizeDomainError).filter(Boolean))]
}
const recordsFor = (domain) => {
	const records = Array.isArray(domain.records) && domain.records.length
		? domain.records
		: [{ purpose: 'routing', required: true, type: 'CNAME', name: domain.hostname, value: cnameTarget.value }]
	// Once the certificate is active the DCV records are history; keep the routing (and TXT) records visible.
	return domain.status === 'active' ? records.filter((r) => r.purpose !== 'certificate') : records
}
/** "record" / "records" for the copy around the list — an apex on App Platform has one A record per address. */
const recordNoun = (domain) => (recordsFor(domain).filter((r) => r.required).length > 1 ? 'records' : 'record')
/** castr's own apex note (ALIAS/ANAME alternative, A records) when it sends one. */
const apexNoteFor = (domain) => domain.dns?.note || ''
const recordHint = (record) => RECORD_PURPOSE_LABELS[record.purpose] || ''
const checkedAgo = (domain) => {
	if (!domain.lastCheckedAt) return ''
	const diff = Math.max(0, Date.now() - new Date(domain.lastCheckedAt).getTime())
	if (diff < 60_000) return 'checked just now'
	if (diff < 3_600_000) return `checked ${Math.round(diff / 60_000)} min ago`
	return `checked ${new Date(domain.lastCheckedAt).toLocaleString()}`
}

onMounted(() => dom.load())
</script>

<template>
	<div class="flex flex-col gap-4" data-testid="site-domains">
		<div class="grid grid-cols-1 gap-4 lg:grid-cols-2 items-start">
			<!-- Castr address -->
			<section class="rounded-lg bg-surface-2 p-4 sm:p-5">
				<div class="flex items-center gap-2">
					<h2 class="mb-0 text-headline font-semibold text-surface-text-2">Castr address</h2>
					<StatusPill v-if="castrIsPrimary" pill size="sm" variant="accent">Primary</StatusPill>
				</div>
				<p class="mb-4 mt-1 text-tiny text-surface-7">Your site is always reachable here. It cannot be changed after creation.</p>
				<div class="flex items-center gap-2 rounded-lg bg-surface-3 px-3 py-2.5">
					<icon-base class="shrink-0 text-surface-8" :width="14" :height="14"><icon-globe /></icon-base>
					<span class="min-w-0 flex-1 truncate text-sm text-white" :title="site.url">{{ site.url }}</span>
					<StatusPill v-if="addressPending" pill size="sm" :variant="readiness.failed.value ? 'warning' : 'accent'" :pulse="!readiness.failed.value" data-testid="castr-address-status">
						{{ readiness.failed.value ? 'Not ready' : 'Setting up' }}
					</StatusPill>
					<StatusPill v-else pill size="sm" :variant="site.status === 'active' ? 'success' : 'muted'" data-testid="castr-address-status">{{ site.status === 'active' ? 'Live' : site.status }}</StatusPill>
					<span v-if="addressPending" :title="readiness.lockedTitle">
						<ButtonCopyToClipboard :text="site.url" size="sm" fixed-width="w-auto" disabled />
					</span>
					<ButtonCopyToClipboard v-else :text="site.url" size="sm" fixed-width="w-auto" />
				</div>
				<p v-if="addressPending" class="mb-0 mt-2 text-tiny text-surface-8" data-testid="castr-address-pending">
					{{ readiness.failed.value ? (readiness.detail.value || 'The address could not be set up.') : `${readiness.phaseLabel.value} — the edge needs 1–2 minutes before this address answers.` }}
				</p>
				<div v-if="!castrIsPrimary" class="mt-3 flex flex-wrap items-center justify-between gap-2 text-tiny text-surface-7">
					<span>Primary address: <span class="text-surface-8">{{ canonicalHost }}</span></span>
					<FormButton type="link" size="sm" :is-loading="revertingPrimary" :disabled="revertingPrimary" @click="revertPrimary">Use the Castr address as primary</FormButton>
				</div>
			</section>

			<!-- Add a custom domain -->
			<section class="rounded-lg bg-surface-2 p-4 sm:p-5">
				<div class="flex items-center gap-2">
					<h2 class="mb-0 text-headline font-semibold text-surface-text-2">Connect a custom domain</h2>
					<StatusPill v-if="locked && !dom.unconfigured.value" pill size="sm" variant="muted" data-testid="domains-not-in-plan-pill">Not in your plan</StatusPill>
					<StatusPill v-else-if="dom.loaded.value && !dom.unconfigured.value" pill size="sm" variant="muted">{{ dom.domains.value.length }} / {{ dom.meta.value.max }}</StatusPill>
				</div>
				<p class="mb-4 mt-1 text-tiny text-surface-7">
					Serve the site on your own domain, for example <span class="text-surface-8">watch.yourbrand.com</span>.
					<template v-if="apexViaARecords">You add one CNAME record at your DNS provider (A records for a root domain); the certificate is issued automatically.</template>
					<template v-else>You add one CNAME record at your DNS provider; the certificate is issued automatically.</template>
				</p>

				<Alert v-if="dom.unconfigured.value" type="warning2" data-testid="domains-unconfigured">
					Custom domains are not enabled on this environment yet. Your Castr address keeps working; contact support if you need a custom domain now.
				</Alert>

				<form v-else class="flex flex-col gap-2" novalidate @submit.prevent="submit">
					<div class="flex flex-col gap-2 sm:flex-row sm:items-end">
						<FormInput id="site-domain-hostname" v-model="hostnameInput" label="Domain" placeholder="e.g. watch.yourbrand.com" input-classes="placeholder:text-surface-6"
							:maxlength="300" :is-error="showValidation || Boolean(addError)"
							:disabled="locked || !dom.canAdd.value || adding" class="sm:flex-1" @blur="touched = true" />
						<FormButton size="md" html-type="submit" class="shrink-0 justify-center" :disabled="!locked && !limitReached && !canSubmit" :is-loading="adding"
							data-testid="domain-add">
							<icon-base v-if="locked || limitReached" class="mr-1.5" :width="14" :height="14" fill="none"><icon-lock /></icon-base>
							Add domain
						</FormButton>
					</div>
					<p v-if="locked" class="mb-0 text-tiny text-surface-7" data-testid="domains-not-in-plan">
						Custom domains are not included in your current plan. Your site stays on its Castr address;
						<button type="button" class="underline" @click="showUpgrade('plan')">upgrade</button> to serve it on your own domain.
					</p>
					<p v-else-if="limitReached" class="mb-0 text-tiny text-surface-7" data-testid="domains-limit-reached">
						Your plan allows {{ dom.meta.value.max }} {{ domainsNoun(dom.meta.value.max) }} per site and this site uses {{ dom.domains.value.length }}.
						<button type="button" class="underline" @click="showUpgrade('limit')">Upgrade</button> to connect more, or remove one first.
					</p>
					<p v-else-if="showValidation" class="mb-0 text-tiny text-error" data-testid="domain-validation">{{ validation.message }}</p>
					<p v-else-if="addError" class="mb-0 text-tiny text-error" data-testid="domain-add-error">{{ addError }}</p>
					<p v-else-if="apexNote && apexViaARecords" class="mb-0 text-tiny text-surface-7" data-testid="domain-apex-note">
						<span class="text-surface-8">Root domain:</span> a bare domain like {{ normalized }} cannot carry a CNAME, so after you add it Castr lists A records to create instead (or one ALIAS / ANAME record where your DNS provider offers it). A subdomain such as <span class="text-surface-8">www.{{ normalized }}</span> works with a single CNAME.
					</p>
					<p v-else-if="apexNote" class="mb-0 text-tiny text-surface-7" data-testid="domain-apex-note">
						<span class="text-surface-8">Root domain:</span> most DNS providers do not allow a CNAME on a bare domain like {{ normalized }}. Use a subdomain such as <span class="text-surface-8">www.{{ normalized }}</span> or <span class="text-surface-8">watch.{{ normalized }}</span>, or a provider that supports CNAME flattening / ALIAS records (Cloudflare, Route 53, DNSimple, NS1).
					</p>
					<p v-else-if="apexViaARecords" class="mb-0 text-tiny text-surface-7">Subdomains use one CNAME record. Root domains use A records (or an ALIAS / ANAME record) instead.</p>
					<p v-else class="mb-0 text-tiny text-surface-7">Subdomains work everywhere. Root domains need a provider with CNAME flattening or ALIAS records.</p>
				</form>
			</section>
		</div>

		<Alert v-if="dom.error.value" type="error" data-testid="domains-load-error">
			Could not load your custom domains{{ dom.error.value.message ? ` (${dom.error.value.message})` : '' }}.
			<FormButton type="link" size="sm" class="ml-2" @click="dom.load()">Retry</FormButton>
		</Alert>

		<Spinner v-if="dom.loading.value && !dom.loaded.value" text="Loading domains…" classes="text-surface-8 mt-8" spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<!-- Domain cards -->
		<section v-else-if="dom.domains.value.length" class="flex flex-col gap-3" data-testid="site-domain-list">
			<div class="flex flex-wrap items-center justify-between gap-2 px-1">
				<h2 class="mb-0 text-headline font-semibold text-surface-text-2">Your domains</h2>
				<span class="text-tiny text-surface-7">
					{{ activeCount }} active
					<template v-if="dom.hasPending.value && locked"> · <span data-testid="domains-verification-paused">verification paused on your current plan</span></template>
					<template v-else-if="dom.hasPending.value"> · pending domains are re-checked every {{ pollSeconds }} s<span v-if="dom.refreshing.value">…</span></template>
				</span>
			</div>

			<ul class="m-0 flex list-none flex-col gap-3 p-0">
				<li v-for="domain in dom.domains.value" :key="domain.hostname" class="rounded-lg bg-surface-2" :data-testid="`domain-card-${domain.hostname}`">
					<div class="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-5">
						<icon-base class="shrink-0 text-surface-8" :width="14" :height="14"><icon-globe /></icon-base>
						<button type="button" class="min-w-0 flex-1 truncate text-left text-sm font-medium text-white hover:underline" :title="isExpanded(domain) ? 'Hide DNS records' : 'Show DNS records'" @click="toggle(domain)">
							{{ domain.hostname }}
						</button>
						<StatusPill pill size="sm" :variant="domainPhaseVariant(domain)" :pulse="domainPhase(domain) === 'pending_ssl' || isBusy(domain, 'verify')" data-testid="domain-status">
							{{ domainPhaseLabel(domain) }}
						</StatusPill>
						<StatusPill v-if="domain.isPrimary" pill size="sm" variant="accent">Primary</StatusPill>
						<div class="flex shrink-0 items-center gap-1">
							<FormButton v-if="domain.status !== 'active'" type="secondary" size="sm" :is-loading="isBusy(domain, 'verify')" :disabled="isBusy(domain)" @click="check(domain)">
								<icon-base v-if="locked" class="mr-1" :width="12" :height="12" fill="none"><icon-lock /></icon-base>
								Check now
							</FormButton>
							<FormButton v-else-if="!domain.isPrimary" type="secondary" size="sm" :is-loading="isBusy(domain, 'primary')" :disabled="isBusy(domain)" @click="makePrimary(domain)">
								<icon-base v-if="locked" class="mr-1" :width="12" :height="12" fill="none"><icon-lock /></icon-base>
								Set as primary
							</FormButton>
							<FormButton is-icon type="danger" size="sm" title="Remove" :disabled="isBusy(domain)" @click="requestRemove(domain)">
								<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
							</FormButton>
						</div>
					</div>

					<p v-if="locked" class="mb-0 flex items-start gap-1.5 border-t border-surface-3 px-4 py-2.5 text-tiny text-surface-7 sm:px-5" :data-testid="`domain-locked-note-${domain.hostname}`">
						<icon-base class="mt-0.5 shrink-0 text-surface-8" :width="12" :height="12" fill="none" aria-hidden="true"><icon-lock /></icon-base>
						<span v-if="domain.status === 'active'">
							Keeps working on your current plan. Custom domains aren't included, so adding domains or changing the primary needs an
							<button type="button" class="underline" @click="showUpgrade('plan')">upgrade</button>.
						</span>
						<span v-else>
							Verification paused — custom domains aren't included in your current plan. Your DNS records stay valid;
							<button type="button" class="underline" @click="showUpgrade('plan')">upgrade</button> to finish connecting this domain.
						</span>
					</p>

					<div v-if="isExpanded(domain)" class="border-t border-surface-3 px-4 py-3 sm:px-5">
						<p class="mb-3 text-tiny text-surface-7">
							<template v-if="domainPhase(domain) === 'pending_dns'">
								Create {{ recordNoun(domain) === 'records' ? 'these records' : 'this record' }} at your DNS provider. Once {{ recordNoun(domain) === 'records' ? 'they resolve' : 'it resolves' }}, Castr verifies the domain and requests the certificate — usually within a few minutes, up to 24 h for slow DNS.
							</template>
							<template v-else-if="domainPhase(domain) === 'pending_ssl'">
								DNS is in place. The certificate is being issued; this usually completes within minutes. Keep the {{ recordNoun(domain) }} below.
							</template>
							<template v-else-if="domainPhase(domain) === 'failed'">
								Verification failed. Fix the {{ recordNoun(domain) }} below (or remove and re-add the domain), then check again.
							</template>
							<template v-else>
								This domain is live<template v-if="domain.activatedAt"> since {{ new Date(domain.activatedAt).toLocaleString() }}</template>. Keep the {{ recordsFor(domain).some((r) => r.type === 'A') ? 'A' : 'CNAME' }} {{ recordNoun(domain) }} in place.
							</template>
							<span v-if="checkedAgo(domain)" class="text-surface-6"> · {{ checkedAgo(domain) }}</span>
						</p>

						<ul class="m-0 flex list-none flex-col gap-2 p-0">
							<li v-for="(record, i) in recordsFor(domain)" :key="`${record.type}-${record.name}-${i}`" class="rounded-lg bg-surface-3 px-3 py-2.5" :data-testid="`dns-record-${record.purpose}`">
								<div class="mb-1.5 flex flex-wrap items-center gap-2 text-tiny">
									<span class="rounded bg-surface-4 px-1.5 py-0.5 font-mono font-medium text-white">{{ record.type }}</span>
									<span class="text-surface-7">{{ recordHint(record) }}</span>
								</div>
								<div class="grid grid-cols-1 gap-2 md:grid-cols-2">
									<div class="flex min-w-0 items-center gap-2">
										<span class="w-10 shrink-0 text-tiny text-surface-7">{{ record.type === 'HTTP' ? 'URL' : 'Name' }}</span>
										<code class="min-w-0 flex-1 truncate text-tiny text-white" :title="record.name">{{ record.name }}</code>
										<ButtonCopyToClipboard :text="record.name" size="sm" fixed-width="w-auto" />
									</div>
									<div class="flex min-w-0 items-center gap-2">
										<span class="w-10 shrink-0 text-tiny text-surface-7">{{ record.type === 'HTTP' ? 'Body' : 'Value' }}</span>
										<code class="min-w-0 flex-1 truncate text-tiny text-white" :title="record.value">{{ record.value }}</code>
										<ButtonCopyToClipboard :text="record.value" size="sm" fixed-width="w-auto" />
									</div>
								</div>
							</li>
						</ul>

						<ul v-if="domain.status !== 'active' && errorsFor(domain).length" class="m-0 mt-3 flex list-none flex-col gap-1 p-0" data-testid="domain-errors">
							<li v-for="(msg, i) in errorsFor(domain)" :key="i" class="text-tiny" :class="domainPhase(domain) === 'failed' ? 'text-error' : 'text-surface-8'">{{ msg }}</li>
						</ul>

						<p v-if="domainPhase(domain) === 'pending_dns' && apexNoteFor(domain)" class="mb-0 mt-3 text-tiny text-surface-7" data-testid="domain-dns-note">
							<span class="text-surface-8">Root domain:</span> {{ apexNoteFor(domain) }}
						</p>
						<p v-else-if="domainPhase(domain) === 'pending_dns' && isApexDomain(domain.hostname)" class="mb-0 mt-3 text-tiny text-surface-7">
							<span class="text-surface-8">Root domain:</span> if your provider refuses a CNAME on {{ domain.hostname }}, use an ALIAS / ANAME / flattened CNAME record with the same value, or connect a subdomain instead.
						</p>
					</div>
				</li>
			</ul>
		</section>

		<ConfirmModal modal-id="remove-site-domain" modal-type="danger" ok-text="Remove domain" cancel-text="Cancel"
			title="Remove this domain?"
			:message="`${pendingRemove?.hostname || 'This domain'} will stop serving your site${pendingRemove?.isPrimary ? ' and the Castr address becomes the primary address again' : ''}. You can delete the DNS record afterwards.`"
			@modal-confirm="confirmRemove" />
		<SitesUpgradeModal v-model:is-visible="upgradeVisible" :title="upgradeCopy.title" :body="upgradeCopy.body"
			:reason="upgradeReason === 'limit' ? SITES_UPGRADE_REASONS.CUSTOM_DOMAINS_LIMIT_REACHED : SITES_UPGRADE_REASONS.CUSTOM_DOMAINS_NOT_IN_PLAN"
			:site="site" :counts="{ custom_domains_used: dom.domains.value.length, max_custom_domains: dom.meta.value.max }" />
	</div>
</template>
